import type { QueryClient } from '@tanstack/react-query'
import { queryClient } from '../lib/query.ts'
import { createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Outlet, redirect } from '@tanstack/react-router'
import { onboardingComplete, ensureSession } from '../lib/auth.tsx'
import { opt, safeNext, type SignInSearch } from '../lib/routing.ts'
import { clean, num, oneOf, str } from '../lib/search.ts'
import { NotFound } from '../pages/NotFound.tsx'
import { SignInPage } from '../pages/SignIn.tsx'
import { ACCOUNT_TABS } from '../pages/accountTabs.ts'
import { AppFrame } from './AppFrame.tsx'
import { STATUS_ORDER } from '@robotensor/api'
import { api } from '../lib/api.ts'
import { QUICK } from '../lib/robot.ts'
import { oldBrowseTarget, oldStatus, resultHref } from './oldAddresses.ts'
import { recordingsSearch } from '../lib/ego/recordingRows.ts'

/**
 * The contributor app (horizon.robotensor.ai). Staff pages live in the ops site (src/ops), built separately, so
 * none of their code ships here.
 */
declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /** Breadcrumb label for this route. */
    crumb?: string
    /** Show this path param as a mono crumb (ids). */
    crumbParam?: string
    /** Section label shown before the first crumb. */
    section?: string
  }
  interface Register {
    router: typeof router
  }
}

export interface RouterContext {
  queryClient: QueryClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: NotFound,
})

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin',
  validateSearch: (s: Record<string, unknown>): SignInSearch =>
    clean({ next: safeNext(s.next), reason: oneOf(s.reason, ['expired', 'signed-out', 'provider'] as const), email: str(s.email) }),
  beforeLoad: async ({ search }: { search: SignInSearch }) => {
    const s = await ensureSession()
    if (s) throw redirect({ href: search.next ?? '/home' })
  },
  component: SignInPage,
})

const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  // `step=phone`: the phone step (W11), also where Start lands when there's no phone yet
  validateSearch: (s: Record<string, unknown>): { next?: string; step?: 'phone' } => clean({ next: safeNext(s.next), step: oneOf(s.step, ['phone'] as const) }),
  beforeLoad: async ({ location }) => {
    const s = await ensureSession()
    if (!s) throw redirect({ to: '/signin', search: { next: location.href } })
  },
  component: lazyRouteComponent(() => import('../pages/Welcome.tsx'), 'WelcomePage'),
})

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ location }) => {
    const s = await ensureSession()
    if (!s) throw redirect({ to: '/signin', search: { next: location.href } })
    if (!onboardingComplete(s.user.onboarding)) throw redirect({ to: '/welcome', search: { next: location.href } })
  },
  component: AppFrame,
})

const page = <P extends string>(path: P) => ({ getParentRoute: () => appRoute, path }) as const

// Home is /home: on horizon.robotensor.ai the gateway serves / from the public site (deploy/gateway/Caddyfile), so the
// workspace's own front page has an address of its own. / here only forwards a navigation inside the app.
const homeRoute = createRoute({ ...page('/home'), staticData: { crumb: 'Home' }, component: lazyRouteComponent(() => import('../pages/Home.tsx'), 'HomePage') })
const rootIndexRoute = createRoute({ ...page('/'), beforeLoad: () => { throw redirect({ to: '/home' }) } })

// ── Robot (W02–W05) ────────────────────────────────────────────────────────────────────────────────────────────
/** Robot › Tasks: search, quick filter, area, the open brief and the scroll position, all in the URL, so "Back to task
 * list" on a result lands exactly where you were. */
const robotSearch = opt((s) => ({ q: str(s.q), f: oneOf(s.f, QUICK), area: str(s.area), task: str(s.task), y: num(s.y) }))

const robotRoute = createRoute({
  ...page('/robot'),
  staticData: { crumb: 'Robot' },
  validateSearch: robotSearch,
  component: lazyRouteComponent(() => import('../pages/robot/Tasks.tsx'), 'RobotTasksPage'),
})

const robotRunsRoute = createRoute({
  ...page('/robot/runs'),
  staticData: { crumb: 'My runs' },
  validateSearch: opt((s) => ({ status: oneOf(s.status, STATUS_ORDER) })),
  component: lazyRouteComponent(() => import('../pages/robot/Runs.tsx'), 'RobotRunsPage'),
})

/** `back`: the task list address to return to (filters and scroll). `after`: opened right after Submit. */
const robotResultRoute = createRoute({
  ...page('/robot/runs/$runId'),
  staticData: { crumb: 'Run', crumbParam: 'runId' },
  validateSearch: opt((s) => ({ back: safeNext(s.back), after: s.after === true || s.after === '1' || s.after === 1 ? true : undefined })),
  component: lazyRouteComponent(() => import('../pages/robot/Result.tsx'), 'RunResultPage'),
})

/** Brief + call + cockpit in one route. It never claims a task on load: opened from a link, it shows the brief with
 * Start; only a Start pressed in the app (navigation state) begins at once. */
const robotSessionRoute = createRoute({
  ...page('/robot/run/$taskId'),
  staticData: { crumb: 'Robot session' },
  validateSearch: opt((s) => ({ back: safeNext(s.back) })),
  component: lazyRouteComponent(() => import('../pages/robot/Session.tsx'), 'RobotSessionPage'),
})

// Addresses from before the redesign (emails, the phone app and bookmarks still use them). Nothing redirects into a
// session start; an old task link lands on the task list with that task's brief open.
const legacySearch = (s: Record<string, unknown>) => clean({ q: str(s.q), path: str(s.path), kind: str(s.kind), bookmarked: s.bookmarked === true || s.bookmarked === 'true' || s.bookmarked === '1' ? true : undefined })
function toNewHome(search: ReturnType<typeof legacySearch>): never {
  const target = oldBrowseTarget(search)
  if (target.to === '/recordings') throw redirect({ to: '/recordings' })
  if (target.to === '/account/work') throw redirect({ to: '/account/$tab', params: { tab: 'work' } })
  throw redirect({ to: '/robot', search: target.search })
}

const exploreRoute = createRoute({ ...page('/explore'), validateSearch: legacySearch, beforeLoad: ({ search }) => toNewHome(search) })
const missionsRoute = createRoute({ ...page('/missions'), validateSearch: legacySearch, beforeLoad: ({ search }) => toNewHome(search) })
const missionRoute = createRoute({
  ...page('/missions/$missionId'),
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/robot', search: { task: params.missionId } })
  },
})
const pathsRoute = createRoute({
  ...page('/paths'),
  beforeLoad: () => {
    throw redirect({ to: '/account/$tab', params: { tab: 'work' } })
  },
})
const pathRoute = createRoute({
  ...page('/paths/$key'),
  beforeLoad: () => {
    throw redirect({ to: '/account/$tab', params: { tab: 'work' } })
  },
})

interface ActivitySearch {
  status?: string
  domain?: string
  item?: string
}
const activityRoute = createRoute({
  ...page('/activity'),
  validateSearch: opt((s): ActivitySearch => ({ status: str(s.status), domain: str(s.domain), item: str(s.item) })),
  beforeLoad: ({ search }: { search: ActivitySearch }) => {
    if (search.item) throw redirect({ to: '/activity/$contributionId', params: { contributionId: search.item } })
    if (search.domain === 'realdata') throw redirect({ to: '/recordings' })
    throw redirect({ to: '/robot/runs', search: clean({ status: oldStatus(search.status) }) })
  },
})

/** Notification links from before (`/activity/<contribution id>`) open that result: a robot run's page, or Recordings. */
const activityItemRoute = createRoute({
  ...page('/activity/$contributionId'),
  beforeLoad: async ({ params }) => {
    const c = await api.contributions.get(params.contributionId).catch(() => null)
    throw redirect({ href: resultHref(c) })
  },
})

// Recordings uploaded from the phone app, reviewed and labelled at a desk (plan §17). Nothing is uploaded here.
const recordingsRoute = createRoute({
  ...page('/recordings'),
  staticData: { crumb: 'Recordings' },
  validateSearch: opt(recordingsSearch),
  component: lazyRouteComponent(() => import('../pages/Recordings.tsx'), 'RecordingsPage'),
})

const recordingRoute = createRoute({
  ...page('/recordings/$captureId'),
  staticData: { crumb: 'Recordings', crumbParam: 'captureId' },
  component: lazyRouteComponent(() => import('../pages/recording/Recording.tsx'), 'RecordingPage'),
})

// Wallet (redesign PW1–PW4): balance, weeks, withdrawals. /earnings and /rewards are its old addresses.
const walletRoute = createRoute({ ...page('/wallet'), staticData: { crumb: 'Wallet' }, component: lazyRouteComponent(() => import('../pages/wallet/Wallet.tsx'), 'WalletPage') })
const walletWeekRoute = createRoute({
  ...page('/wallet/week/$epochId'),
  staticData: { crumb: 'Wallet', crumbParam: 'epochId' },
  component: lazyRouteComponent(() => import('../pages/wallet/WeekStatement.tsx'), 'WeekStatementPage'),
})
const earningsRoute = createRoute({
  ...page('/earnings'),
  beforeLoad: () => {
    throw redirect({ to: '/wallet' })
  },
})
const rewardsRoute = createRoute({
  ...page('/rewards'),
  beforeLoad: () => {
    throw redirect({ to: '/wallet' })
  },
})

const accountRoute = createRoute({ ...page('/account'), staticData: { crumb: 'Account' }, component: lazyRouteComponent(() => import('../pages/Account.tsx'), 'AccountPage') })

const accountTabRoute = createRoute({
  ...page('/account/$tab'),
  staticData: { crumb: 'Account' },
  beforeLoad: ({ params }) => {
    if (!(ACCOUNT_TABS as readonly string[]).includes(params.tab)) throw redirect({ to: '/account' })
  },
  component: lazyRouteComponent(() => import('../pages/Account.tsx'), 'AccountPage'),
})

// Addresses from before the account pages merged (emails, the phone app and bookmarks still use them).
const profileRoute = createRoute({
  ...page('/profile'),
  validateSearch: (s: Record<string, unknown>): { tab?: string } => clean({ tab: str(s.tab) }),
  beforeLoad: () => {
    throw redirect({ to: '/account/$tab', params: { tab: 'profile' } })
  },
})
const devicesRoute = createRoute({
  ...page('/devices'),
  beforeLoad: () => {
    throw redirect({ to: '/account/$tab', params: { tab: 'devices' } })
  },
})

// Robot control's old page (`/sim/control?mission=…`): the task list with that task's brief open. Ops keeps its own
// /sim/control (pages/SimControl.tsx) for staff playing tasks; the search below stays so that page's types hold.
interface SimControlSearch {
  assignment?: string
  task?: string
  handoff?: string
  mission?: string
  pairing?: string
}
const simControlSearch = opt((s): SimControlSearch => ({ assignment: str(s.assignment), task: str(s.task), handoff: str(s.handoff), mission: str(s.mission), pairing: str(s.pairing) }))
const simControlRoute = createRoute({
  ...page('/sim/control'),
  validateSearch: simControlSearch,
  beforeLoad: ({ search }: { search: SimControlSearch }) => {
    throw redirect({ to: '/robot', search: search.mission ? { task: search.mission } : {} })
  },
})

// Pages a phone or a computer reaches without being signed in here (control modes; each checks its own mode).
const pairRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/p/$token',
  component: lazyRouteComponent(() => import('../pages/Handoff.tsx'), 'PairPage'),
})

const goRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/go',
  validateSearch: (s: Record<string, unknown>): { code?: string } => clean({ code: str(s.code) }),
  component: lazyRouteComponent(() => import('../pages/Handoff.tsx'), 'GoPage'),
})

// The "This wasn't me" link from a payout-wallet email: works signed out (the token is in the fragment).
const walletCancelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/wallet/cancel',
  component: lazyRouteComponent(() => import('../pages/WalletCancel.tsx'), 'WalletCancelPage'),
})

const routeTree = rootRoute.addChildren([
  signInRoute,
  welcomeRoute,
  pairRoute,
  goRoute,
  walletCancelRoute,
  appRoute.addChildren([
    homeRoute, rootIndexRoute, robotRoute, robotRunsRoute, robotResultRoute, robotSessionRoute,
    exploreRoute, missionsRoute, pathsRoute, pathRoute, missionRoute, activityRoute, activityItemRoute,
    recordingsRoute, recordingRoute, walletRoute, walletWeekRoute, earningsRoute, rewardsRoute, accountRoute, accountTabRoute,
    simControlRoute, profileRoute, devicesRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: false,
  defaultNotFoundComponent: NotFound,
})
